import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Phone, Mail, Copy, Check, Shield, Share2 } from 'lucide-react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function InviteFriendDialog({ open, onOpenChange }) {
  const [channel, setChannel] = useState('sms');
  const [toPhone, setToPhone] = useState('');
  const [toEmail, setToEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [message, setMessage] = useState("Hey! I'd love to chat with you privately on Privluxe. Join me!");
  const [inviteCopied, setInviteCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const resetState = () => {
    setInviteCopied(false);
    setToPhone('');
    setToEmail('');
    setChannel('sms');
    setSenderName('');
    setMessage("Hey! I'd love to chat with you privately on Privluxe. Join me!");
  };

  const copyInvite = async () => {
    try {
      // First create the invite in backend to get the token
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/invite/create`, {
        method: 'link',
        message: message,
        sender_name: senderName || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const inviteUrl = response.data.invite_url;
      const displayName = senderName || user?.username || 'Someone';
      
      // Shorter, cleaner invite message
      const inviteMessage = `${displayName} invited you to Privluxe\n\n${inviteUrl}`;

      // Detect mobile devices - prioritize Web Share API on mobile
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // On mobile: Use Web Share API first (opens native share sheet → WhatsApp directly)
      // This is more reliable than clipboard on iOS Safari
      if (isMobile && navigator.share) {
        try {
          await navigator.share({
            title: 'Privluxe Invite',
            text: inviteMessage,
          });
          setInviteCopied(true);
          toast.success('Shared successfully!');
          setTimeout(() => setInviteCopied(false), 3000);
          return; // Exit early on successful share
        } catch (shareError) {
          // User cancelled or share failed - fall through to clipboard
          if (shareError.name !== 'AbortError') {
            console.log('Share API failed, trying clipboard:', shareError);
          } else {
            // User cancelled the share dialog
            return;
          }
        }
      }
      
      // Desktop or Share API failed: Use clipboard
      let copied = false;
      
      // Method 1: Modern Clipboard API
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        try {
          await navigator.clipboard.writeText(inviteMessage);
          copied = true;
        } catch (e) {
          console.log('Clipboard API failed:', e);
        }
      }
      
      // Method 2: Fallback for older browsers
      if (!copied) {
        try {
          const textArea = document.createElement('textarea');
          textArea.value = inviteMessage;
          textArea.style.cssText = 'position:fixed;left:0;top:0;width:1px;height:1px;padding:0;border:none;outline:none;boxShadow:none;background:transparent;opacity:0;';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          textArea.setSelectionRange(0, inviteMessage.length);
          copied = document.execCommand('copy');
          document.body.removeChild(textArea);
        } catch (e2) {
          console.log('execCommand fallback failed:', e2);
          copied = false;
        }
      }
      
      if (copied) {
        setInviteCopied(true);
        toast.success('Invite copied! Paste in WhatsApp');
        setTimeout(() => setInviteCopied(false), 3000);
      } else {
        // Nothing worked - show manual instruction
        toast.error('Tap and hold to copy: ' + inviteUrl);
      }
    } catch (error) {
      console.error('Failed to create invite:', error);
      toast.error('Failed to create invite link');
    }
  };

  const sendInvite = async () => {
    if (channel === 'sms' && !toPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    if (channel === 'email' && !toEmail) {
      toast.error('Please enter an email address');
      return;
    }
    if (!message) {
      toast.error('Please enter a message');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/invite/send`, {
        channel: channel,
        recipient_phone: channel === 'sms' ? toPhone : null,
        recipient_email: channel === 'email' ? toEmail : null,
        message: message,
        sender_name: senderName || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Invite sent via ${channel === 'sms' ? 'SMS' : 'Email'}!`);
      onOpenChange(false);
      resetState();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
        if (!isOpen) resetState();
      }}
    >
      <DialogContent 
        className="max-w-[calc(100%-2rem)] sm:max-w-md mx-auto border-0 max-h-[90vh] overflow-y-auto"
        style={{ 
          background: 'rgba(10, 15, 44, 0.98)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
        data-testid="invite-friend-dialog"
      >
        <DialogHeader>
          <DialogTitle style={{ color: '#E5E7EB' }}>Invite a Friend</DialogTitle>
          <DialogDescription style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
            Send an anonymous invite via SMS, Email, or copy link
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {/* Channel Tabs */}
          <div className="flex rounded-lg overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <button
              onClick={() => setChannel('sms')}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-all ${channel === 'sms' ? 'text-white' : 'text-gray-400'}`}
              style={{ background: channel === 'sms' ? 'rgba(124, 58, 237, 0.3)' : 'transparent' }}
              data-testid="invite-tab-sms"
            >
              <Phone className="h-4 w-4 mx-auto mb-1" />
              SMS
            </button>
            <button
              onClick={() => setChannel('email')}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-all ${channel === 'email' ? 'text-white' : 'text-gray-400'}`}
              style={{ background: channel === 'email' ? 'rgba(124, 58, 237, 0.3)' : 'transparent' }}
              data-testid="invite-tab-email"
            >
              <Mail className="h-4 w-4 mx-auto mb-1" />
              Email
            </button>
            <button
              onClick={() => setChannel('copy')}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-all ${channel === 'copy' ? 'text-white' : 'text-gray-400'}`}
              style={{ background: channel === 'copy' ? 'rgba(124, 58, 237, 0.3)' : 'transparent' }}
              data-testid="invite-tab-copy"
            >
              <Copy className="h-4 w-4 mx-auto mb-1" />
              Copy Link
            </button>
          </div>

          {/* SMS Tab Content */}
          {channel === 'sms' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label style={{ color: '#E5E7EB' }}>Recipient Phone *</Label>
                <PhoneInput
                  international
                  defaultCountry="IN"
                  value={toPhone}
                  onChange={setToPhone}
                  className="phone-input-dark"
                  data-testid="recipient-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label style={{ color: '#E5E7EB' }}>Your Name (how they'll know you)</Label>
                <Input
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder="e.g., A friend, Your secret admirer..."
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12"
                  data-testid="sender-name-input"
                />
                <p className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.4)' }}>
                  Leave blank to send as "Someone"
                </p>
              </div>
              <div className="space-y-2">
                <Label style={{ color: '#E5E7EB' }}>Message *</Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your invite message..."
                  rows={3}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 resize-none"
                  data-testid="message-textarea"
                />
              </div>
              <div 
                className="p-3 rounded-lg text-xs flex items-center gap-2"
                style={{ background: 'rgba(124, 58, 237, 0.1)', border: '1px solid rgba(124, 58, 237, 0.2)' }}
              >
                <Shield className="h-4 w-4 shrink-0" style={{ color: '#A78BFA' }} />
                <span style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
                  Your phone number stays hidden. SMS sent from our system.
                </span>
              </div>
              <p className="text-xs text-center" style={{ color: '#F59E0B' }}>
                This invite will expire in 48 hours
              </p>
              <Button 
                onClick={sendInvite} 
                className="w-full text-white font-medium h-12"
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                disabled={loading || !toPhone || !message} 
                data-testid="send-sms-button"
              >
                {loading ? 'Sending...' : 'Send Invite via SMS'}
              </Button>
            </div>
          )}

          {/* Email Tab Content */}
          {channel === 'email' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label style={{ color: '#E5E7EB' }}>Recipient Email *</Label>
                <Input
                  type="email"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  placeholder="friend@email.com"
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12"
                  data-testid="recipient-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label style={{ color: '#E5E7EB' }}>Your Name (how they'll know you)</Label>
                <Input
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder="e.g., A friend, Your secret admirer..."
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12"
                  data-testid="sender-name-email-input"
                />
                <p className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.4)' }}>
                  Leave blank to send as "Someone"
                </p>
              </div>
              <div className="space-y-2">
                <Label style={{ color: '#E5E7EB' }}>Message *</Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your invite message..."
                  rows={3}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 resize-none"
                  data-testid="message-textarea-email"
                />
              </div>
              <div 
                className="p-3 rounded-lg text-xs flex items-center gap-2"
                style={{ background: 'rgba(124, 58, 237, 0.1)', border: '1px solid rgba(124, 58, 237, 0.2)' }}
              >
                <Shield className="h-4 w-4 shrink-0" style={{ color: '#A78BFA' }} />
                <span style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
                  Your email stays hidden. Email sent from our system.
                </span>
              </div>
              <p className="text-xs text-center" style={{ color: '#F59E0B' }}>
                This invite will expire in 48 hours
              </p>
              <Button 
                onClick={sendInvite} 
                className="w-full text-white font-medium h-12"
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                disabled={loading || !toEmail || !message} 
                data-testid="send-email-button"
              >
                {loading ? 'Sending...' : 'Send Invite via Email'}
              </Button>
            </div>
          )}

          {/* Copy Link Tab Content */}
          {channel === 'copy' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label style={{ color: '#E5E7EB' }}>How they'll know you</Label>
                <Input
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder={user?.username || "Your name"}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12"
                  data-testid="copy-sender-name-input"
                />
                <p className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.4)' }}>
                  Leave blank to use your username "{user?.username}"
                </p>
              </div>
              <div 
                className="p-4 rounded-lg text-sm"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <p style={{ color: 'rgba(229, 231, 235, 0.8)' }}>
                  Hey! <span style={{ color: '#A78BFA' }}>{senderName || user?.username || 'Someone'}</span> invited you to a private chat on Privluxe.
                </p>
                <p className="mt-2" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>
                  Your messages will be end-to-end encrypted and self-destruct.
                </p>
                <p className="mt-2 text-xs" style={{ color: 'rgba(124, 58, 237, 0.8)' }}>
                  + Unique secure invite link
                </p>
              </div>
              <p className="text-xs text-center" style={{ color: '#F59E0B' }}>
                This invite will expire in 48 hours
              </p>
              <Button 
                onClick={copyInvite}
                className="w-full text-white font-medium h-12"
                style={{ 
                  background: inviteCopied 
                    ? 'rgba(34, 197, 94, 0.3)' 
                    : 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)',
                  border: inviteCopied ? '1px solid rgba(34, 197, 94, 0.5)' : 'none'
                }}
                data-testid="copy-invite-button"
              >
                {inviteCopied ? (
                  <><Check className="h-4 w-4 mr-2" /> Shared!</>
                ) : (
                  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ? (
                    <><Share2 className="h-4 w-4 mr-2" /> Share Invite</>
                  ) : (
                    <><Copy className="h-4 w-4 mr-2" /> Copy Invite Link</>
                  )
                )}
              </Button>
              <p className="text-center text-xs" style={{ color: 'rgba(229, 231, 235, 0.4)' }}>
                Share via any app - WhatsApp, Telegram, iMessage, etc.
              </p>
            </div>
          )}
        </div>

        {/* Custom styles for phone input */}
        <style>{`
          .phone-input-dark .PhoneInputInput {
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 0.5rem;
            padding: 0.75rem 1rem;
            color: white;
            font-size: 16px;
            height: 48px;
          }
          .phone-input-dark .PhoneInputInput:focus {
            border-color: #7C3AED;
            outline: none;
          }
          .phone-input-dark .PhoneInputInput::placeholder {
            color: rgba(156, 163, 175, 0.5);
          }
          .phone-input-dark .PhoneInputCountrySelect {
            background: rgba(255, 255, 255, 0.05);
            color: white;
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}

export default InviteFriendDialog;
